/**
 * #8954 S3 (P2 proof EPIC) — a case study quotes its client.
 *
 * A portfolio item names its client (`client`). A published CMS testimonial names the client's
 * company (`client_company`). The portfolio detail page shows the newest published testimonial
 * whose company matches, so a client quote captured in the portal (S2, a staff-published draft)
 * shows on that client's case study with no extra link field and no schema change.
 *
 * Consent: only a testimonial with `show_company_name` set may match, because placing it on a
 * page that names the company would reveal the company. The person's name shows only when
 * `show_client_name` is set. Pure apart from `fetchPublishedTestimonials`.
 */

export interface CmsTestimonial {
  id: number;
  client_name?: string | null;
  client_company?: string | null;
  client_role?: string | null;
  quote?: string | null;
  status?: string | null;
  featured?: number | boolean | null;
  show_client_name?: number | boolean | null;
  show_company_name?: number | boolean | null;
  published_date?: string | null;
  updated_at?: string | null;
}

export interface ClientQuote {
  quote: string;
  /** "Name, Role" or "Name" when the client agreed to show the name; else null. */
  attribution: string | null;
  company: string;
}

const LEGAL_SUFFIX = /\b(inc|incorporated|llc|l\.l\.c|ltd|limited|co|corp|corporation|company|pllc|pc|lp|llp)\b\.?/g;

/** A company name for matching: lowercase, no punctuation, no legal suffix, single spaces. Pure. */
export function normalizeCompany(name: string | null | undefined): string {
  return String(name ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(LEGAL_SUFFIX, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A consent flag is on only for true, 1 or '1'. NULL, false, 0 or anything else is off (fail closed). */
export const on = (v: unknown) => v === true || v === 1 || v === '1';

/**
 * The quote for a portfolio client: the published testimonial whose company matches, with the
 * company-name consent set and a non-empty quote. Featured first, then the newest. Null when none.
 */
export function matchClientQuote(client: string | null | undefined, testimonials: readonly CmsTestimonial[]): ClientQuote | null {
  const want = normalizeCompany(client);
  if (!want) return null;
  const hits = testimonials.filter(
    (t) =>
      (t.status ?? 'published') === 'published' &&
      on(t.show_company_name) &&
      typeof t.quote === 'string' &&
      t.quote.trim().length > 0 &&
      normalizeCompany(t.client_company) === want,
  );
  if (hits.length === 0) return null;
  const when = (t: CmsTestimonial) => Date.parse(t.published_date ?? t.updated_at ?? '') || 0;
  hits.sort((a, b) => Number(on(b.featured)) - Number(on(a.featured)) || when(b) - when(a) || b.id - a.id);
  const t = hits[0];
  const name = on(t.show_client_name) ? (t.client_name ?? '').trim() : '';
  const role = (t.client_role ?? '').trim();
  return {
    quote: t.quote!.trim(),
    attribution: name ? (role ? `${name}, ${role}` : name) : null,
    company: (t.client_company ?? '').trim(),
  };
}

/** Published testimonials from the content API (published only for a public reader). Never throws. */
export async function fetchPublishedTestimonials(fetchImpl: typeof fetch = fetch): Promise<CmsTestimonial[]> {
  try {
    const res = await fetchImpl('/api/content/testimonials?status=published');
    if (!res.ok) return [];
    const body = (await res.json()) as { data?: unknown; items?: unknown } | unknown[];
    const list = Array.isArray(body) ? body : (body as { data?: unknown }).data ?? (body as { items?: unknown }).items;
    return Array.isArray(list) ? (list as CmsTestimonial[]) : [];
  } catch {
    return [];
  }
}
